import { useState, useEffect } from 'react'
import { Brain, MessageSquare, FolderTree, Zap, Shield, ArrowRight, Sparkles, Layers, Globe, Check, Crown, Key, Users, Star, Rocket } from 'lucide-react'
import { useT, useLanguageStore, languageNames, type Language } from '@/i18n'
import { api } from '@/services/api-client'

type PlanData = {
  id: string
  name: string
  price: number
  period: string
  features: string[]
  description: string
  icon: string
  color: string
  is_popular: number
  cta_text: string
  price_label: string
  sort_order: number
}

const ICON_MAP: Record<string, React.ReactNode> = {
  key: <Key size={24} />,
  users: <Users size={24} />,
  crown: <Crown size={24} />,
  zap: <Zap size={24} />,
  shield: <Shield size={24} />,
  star: <Star size={24} />,
  rocket: <Rocket size={24} />,
  sparkles: <Sparkles size={24} />,
}

interface LandingPageProps {
  onNavigate: (view: 'login' | 'register') => void
}

export function LandingPage({ onNavigate }: LandingPageProps) {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null)
  const [dbPlans, setDbPlans] = useState<PlanData[]>([])
  const t = useT()
  const { language, setLanguage } = useLanguageStore()

  useEffect(() => {
    api.getPublicPlans().then(setDbPlans).catch(() => {})
  }, [])

  const features = [
    {
      icon: <Brain size={28} />,
      title: t.featureMultiAiTitle,
      description: t.featureMultiAiDesc,
    },
    {
      icon: <FolderTree size={28} />,
      title: t.featureProjectsTitle,
      description: t.featureProjectsDesc,
    },
    {
      icon: <MessageSquare size={28} />,
      title: t.featureThreadsTitle,
      description: t.featureThreadsDesc,
    },
    {
      icon: <Zap size={28} />,
      title: t.featureFastTitle,
      description: t.featureFastDesc,
    },
    {
      icon: <Shield size={28} />,
      title: t.featureApiTitle,
      description: t.featureApiDesc,
    },
    {
      icon: <Layers size={28} />,
      title: t.featureDecisionsTitle,
      description: t.featureDecisionsDesc,
    },
  ]

  // Hardcoded fallback plans (used when API plans not available)
  const fallbackPlans = [
    { name: t.pricingFreeName, price: t.pricingFreePrice, period: t.pricingFreePeriod, description: t.pricingFreeDesc, icon: <Key size={24} />, color: 'var(--text-secondary)', features: [t.pricingFreeFeat1, t.pricingFreeFeat2, t.pricingFreeFeat3, t.pricingFreeFeat4, t.pricingFreeFeat5, t.pricingFreeFeat6], cta: t.pricingFreeCta, popular: false },
    { name: t.pricingPayName, price: t.pricingPayPrice, period: t.pricingPayPeriod, description: t.pricingPayDesc, icon: <Users size={24} />, color: 'var(--accent-primary)', features: [t.pricingPayFeat1, t.pricingPayFeat2, t.pricingPayFeat3, t.pricingPayFeat4, t.pricingPayFeat5, t.pricingPayFeat6], cta: t.pricingPayCta, popular: false },
    { name: t.pricingPremName, price: t.pricingPremPrice, period: t.pricingPremPeriod, description: t.pricingPremDesc, icon: <Crown size={24} />, color: '#a855f7', features: [t.pricingPremFeat1, t.pricingPremFeat2, t.pricingPremFeat3, t.pricingPremFeat4, t.pricingPremFeat5, t.pricingPremFeat6], cta: t.pricingPremCta, popular: true },
  ]

  const pricingPlans = dbPlans.length > 0
    ? dbPlans.map(p => ({
        name: p.name,
        price: p.price_label || (p.price === 0 ? '$0' : `$${Number(p.price).toFixed(2)}`),
        period: p.period || '',
        description: p.description || '',
        icon: ICON_MAP[p.icon] || <Key size={24} />,
        color: p.color || 'var(--text-secondary)',
        features: p.features || [],
        cta: p.cta_text || 'Get Started',
        popular: !!p.is_popular,
      }))
    : fallbackPlans

  return (
    <div className="landing">
      {/* Header */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="landing-logo">
            <div className="landing-logo-icon">
              <Sparkles size={24} />
            </div>
            <span className="landing-logo-text">Synapsios</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features">{t.landingNavFeatures}</a>
            <a href="#pricing">{t.landingNavPricing}</a>
            <a href="#how">{t.landingNavHow}</a>
          </div>
          <div className="landing-lang-switcher">
            {(Object.keys(languageNames) as Language[]).map(lang => (
              <button
                key={lang}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
                onClick={() => setLanguage(lang)}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="landing-nav-actions">
            <button className="button-secondary" onClick={() => onNavigate('login')}>
              {t.landingSignIn}
            </button>
            <button className="button-primary" onClick={() => onNavigate('register')}>
              {t.landingGetStarted}
              <ArrowRight size={16} />
            </button>
          </div>
        </nav>
      </header>

      <main className="landing-main">
        {/* Hero */}
        <section className="landing-hero">
          <div className="hero-badge">
            <Globe size={14} />
            <span>{t.landingBadge}</span>
          </div>
          <h1 className="hero-title">
            {t.landingTitle1}
            <br />
            <span className="hero-accent">{t.landingTitle2}</span>
          </h1>
          <p className="hero-subtitle">
            {t.landingSubtitle}
          </p>
          <div className="hero-actions">
            <button className="hero-cta-primary" onClick={() => onNavigate('register')}>
              {t.landingStartFree}
              <ArrowRight size={18} />
            </button>
            <button className="hero-cta-secondary" onClick={() => onNavigate('login')}>
              {t.landingHaveAccount}
            </button>
          </div>

          <div className="hero-visual">
            <div className="hero-window">
              <div className="hero-window-bar">
                <div className="hero-dot red" />
                <div className="hero-dot yellow" />
                <div className="hero-dot green" />
                <span className="hero-window-title">{t.landingMockTitle}</span>
              </div>
              <div className="hero-window-body">
                <div className="hero-sidebar-mock">
                  <div className="hero-mock-item active">
                    <FolderTree size={12} />
                    <span>{t.landingMockProject}</span>
                  </div>
                  <div className="hero-mock-item indent">
                    <MessageSquare size={12} />
                    <span>{t.landingMockArch}</span>
                  </div>
                  <div className="hero-mock-item indent">
                    <MessageSquare size={12} />
                    <span>{t.landingMockFrontend}</span>
                  </div>
                  <div className="hero-mock-item">
                    <FolderTree size={12} />
                    <span>{t.landingMockResearch}</span>
                  </div>
                </div>
                <div className="hero-chat-mock">
                  <div className="hero-msg user">{t.landingMockMsg1}</div>
                  <div className="hero-msg assistant">
                    <span className="hero-model-tag">Claude 3.5</span>
                    {t.landingMockReply1}
                  </div>
                  <div className="hero-msg user">{t.landingMockMsg2}</div>
                  <div className="hero-msg assistant">
                    <span className="hero-model-tag">GPT-4</span>
                    {t.landingMockReply2}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="landing-features" id="features">
          <div className="features-header">
            <h2>{t.featuresTitle}</h2>
            <p>{t.featuresSubtitle}</p>
          </div>
          <div className="features-grid">
            {features.map((feature, i) => (
              <div
                key={i}
                className={`feature-card ${hoveredFeature === i ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing */}
        <section className="landing-pricing" id="pricing">
          <div className="pricing-header">
            <h2>{t.pricingTitle}</h2>
            <p>
              {t.pricingSubtitle}
            </p>
          </div>

          <div className="pricing-grid">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
              >
                {plan.popular && <div className="pricing-popular-badge">{t.pricingMostPopular}</div>}
                <div className="pricing-card-header">
                  <div className="pricing-icon" style={{ color: plan.color }}>
                    {plan.icon}
                  </div>
                  <h3>{plan.name}</h3>
                  <div className="pricing-price">
                    <span className="pricing-amount">{plan.price}</span>
                    {plan.period && <span className="pricing-period">/{plan.period}</span>}
                  </div>
                  <p className="pricing-description">{plan.description}</p>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((f, i) => (
                    <li key={i}>
                      <Check size={14} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={plan.popular ? 'hero-cta-primary' : 'hero-cta-secondary'}
                  onClick={() => onNavigate('register')}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {plan.cta}
                  <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* API Leasing explainer */}
          <div className="leasing-explainer">
            <div className="leasing-card">
              <div className="leasing-header">
                <Users size={28} />
                <h3>{t.leasingTitle}</h3>
              </div>
              <p className="leasing-intro">
                {t.leasingIntro}
              </p>
              <div className="leasing-comparison">
                <div className="leasing-option">
                  <div className="leasing-option-header community">
                    <Users size={18} />
                    <span>{t.leasingCommunityTitle}</span>
                  </div>
                  <p>
                    {t.leasingCommunityDesc}
                  </p>
                  <div className="leasing-example">
                    <span className="leasing-label">{t.example}</span>
                    <span>{t.leasingCommunityExample}</span>
                  </div>
                </div>
                <div className="leasing-vs">{t.vs}</div>
                <div className="leasing-option">
                  <div className="leasing-option-header premium">
                    <Crown size={18} />
                    <span>{t.leasingPremiumTitle}</span>
                  </div>
                  <p>
                    {t.leasingPremiumDesc}
                  </p>
                  <div className="leasing-example">
                    <span className="leasing-label">{t.example}</span>
                    <span>{t.leasingPremiumExample}</span>
                  </div>
                </div>
              </div>
              <p className="leasing-tip">
                {t.leasingTip}
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="landing-how" id="how">
          <h2>{t.howTitle}</h2>
          <div className="how-steps">
            <div className="how-step">
              <div className="step-number">1</div>
              <h3>{t.howStep1Title}</h3>
              <p>{t.howStep1Desc}</p>
            </div>
            <div className="how-step">
              <div className="step-number">2</div>
              <h3>{t.howStep2Title}</h3>
              <p>{t.howStep2Desc}</p>
            </div>
            <div className="how-step">
              <div className="step-number">3</div>
              <h3>{t.howStep3Title}</h3>
              <p>{t.howStep3Desc}</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="landing-cta">
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaSubtitle}</p>
          <button className="hero-cta-primary" onClick={() => onNavigate('register')}>
            {t.ctaButton}
            <ArrowRight size={18} />
          </button>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-logo">
            <Sparkles size={16} />
            <span>Synapsios</span>
          </div>
          <p>{t.footerTagline}</p>
          <p className="landing-footer-tech">{t.footerTech}</p>
        </div>
      </footer>
    </div>
  )
}
